/**
 * Promo Poster Generator - Admin Panel
 * Generates branded marketing poster with Top 3 winners (All-Time / Monthly / Weekly).
 * Uses html-to-image to export PNG in 3 formats (Square/Story/Landscape).
 *
 * Design: Dark Premium theme - navy gradient, gold accents, Grras branded.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Download, RefreshCw, Crown, Flame, Sparkles, Trophy,
  Image as ImageIcon, Eye, EyeOff
} from 'lucide-react';
import { API_BASE, getAdminToken } from './adminUtils';

const GRRAS_LOGO_URL = '/images/grras-logo.png';
const GRRAS_WEBSITE = 'lab.grras.tech';
const GRRAS_PHONE = '+91-9001991227';

const FORMATS = [
  { value: 'square', label: 'Square (1080×1080)', w: 1080, h: 1080, desc: 'Instagram, WhatsApp Share' },
  { value: 'story', label: 'Story (1080×1920)', w: 1080, h: 1920, desc: 'WhatsApp Status, IG Story' },
  { value: 'landscape', label: 'Landscape (1200×628)', w: 1200, h: 628, desc: 'LinkedIn, Twitter' }
];

const TIER_COLORS = {
  amber:   { bg: 'from-amber-500 to-orange-500',   text: '#fbbf24' },
  cyan:    { bg: 'from-cyan-400 to-blue-500',      text: '#22d3ee' },
  yellow:  { bg: 'from-yellow-400 to-amber-500',   text: '#facc15' },
  slate:   { bg: 'from-slate-400 to-slate-600',    text: '#cbd5e1' },
  orange:  { bg: 'from-orange-400 to-red-500',     text: '#fb923c' },
  emerald: { bg: 'from-emerald-400 to-teal-500',   text: '#34d399' }
};

const WinnerCard = ({ winner, label, emoji, accentColor, useFullName, sizes }) => {
  if (!winner) {
    return (
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '20px',
        padding: `${sizes.cardPad}px`,
        textAlign: 'center'
      }}>
        <p style={{
          fontSize: `${sizes.label}px`,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.2em',
          color: '#64748b',
          marginBottom: '6px'
        }}>{label}</p>
        <p style={{ color: '#94a3b8', fontSize: `${sizes.statText}px` }}>No activity yet</p>
      </div>
    );
  }
  const tier = TIER_COLORS[winner.tier?.color] || TIER_COLORS.amber;
  const name = useFullName ? winner.full_name : winner.display_name;

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: '20px',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.03) 100%)',
        border: '1px solid rgba(255,255,255,0.15)',
        boxShadow: `0 10px 30px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)`
      }}
    >
      {/* Top accent strip — thicker for visibility */}
      <div
        style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: `${sizes.accentHeight}px`,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}80, transparent)`
        }}
      />
      <div style={{ padding: `${sizes.cardPad}px`, paddingTop: `${sizes.cardPad + 6}px` }}>
        {/* Label row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: `${sizes.emojiGap}px`,
          marginBottom: `${sizes.cardPad - 4}px`
        }}>
          <span style={{ fontSize: `${sizes.emoji}px`, lineHeight: 1 }}>{emoji}</span>
          <span style={{
            fontSize: `${sizes.label}px`,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            color: accentColor
          }}>
            {label}
          </span>
        </div>
        {/* Name + score row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{
              color: 'white',
              fontSize: `${sizes.name}px`,
              fontWeight: 900,
              lineHeight: 1.1,
              margin: 0,
              letterSpacing: '-0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>{name}</p>
            <div style={{
              display: 'flex', alignItems: 'center', gap: `${sizes.statGap}px`,
              marginTop: `${sizes.statMarginTop}px`, flexWrap: 'wrap'
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                color: '#e2e8f0', fontSize: `${sizes.statText}px`, fontWeight: 600,
                whiteSpace: 'nowrap'
              }}>
                <span style={{ color: accentColor, fontSize: `${sizes.statText + 2}px` }}>🏆</span>
                {winner.labs_completed} labs
              </span>
              {winner.mocks_passed > 0 && (
                <span style={{
                  color: '#e2e8f0', fontSize: `${sizes.statText}px`, fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}>
                  🎯 {winner.mocks_passed} mocks
                </span>
              )}
              {winner.streak_days > 0 && (
                <span style={{
                  color: '#fb923c', fontSize: `${sizes.statText}px`, fontWeight: 700,
                  whiteSpace: 'nowrap'
                }}>
                  🔥 {winner.streak_days}d streak
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <p style={{
              color: 'white',
              fontSize: `${sizes.score}px`,
              fontWeight: 900,
              lineHeight: 1,
              margin: 0,
              letterSpacing: '-0.02em'
            }}>
              {winner.mastery_score.toLocaleString()}
            </p>
            <p style={{
              fontSize: `${sizes.scoreLabel}px`,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              color: accentColor,
              marginTop: '4px',
              margin: `4px 0 0 0`
            }}>
              Score
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The actual poster — renders in an off-screen container with fixed dimensions.
 * We scale it visually in preview but export at true size.
 *
 * Sizes are tuned for readability even when compressed by WhatsApp Status / Instagram.
 */
const PosterCanvas = React.forwardRef(({ data, format, useFullName, posterRef }, ref) => {
  const dims = FORMATS.find(f => f.value === format) || FORMATS[0];
  const isLandscape = format === 'landscape';
  const isStory = format === 'story';
  const isSquare = format === 'square';

  // Format-specific size tokens (pixel values — tuned for each canvas)
  // Story = 1080×1920 (WhatsApp Status / Instagram Story — biggest text needed)
  // Square = 1080×1080 (Instagram/WhatsApp Share — medium)
  // Landscape = 1200×628 (LinkedIn/Twitter — compact)
  const sizes = isStory ? {
    logoH: 130,
    topBadge: 24, topBadgeIcon: 38, topBadgePad: '18px 28px',
    h1: 92, h1Accent: 92, subtitle: 32,
    cardPad: 36, accentHeight: 6,
    label: 20, emoji: 46, emojiGap: 16,
    name: 58, score: 78, scoreLabel: 18,
    statText: 28, statGap: 24, statMarginTop: 16,
    proofBadgeText: 22, proofBadgeIcon: 28, proofPad: '22px 28px',
    footerWebsite: 44, footerPhone: 28,
    ribbonText: 20, ribbonPad: '14px 28px',
    pagePad: '80px 70px', gap: 36
  } : isSquare ? {
    logoH: 80,
    topBadge: 16, topBadgeIcon: 26, topBadgePad: '10px 18px',
    h1: 64, h1Accent: 64, subtitle: 22,
    cardPad: 24, accentHeight: 5,
    label: 14, emoji: 32, emojiGap: 10,
    name: 38, score: 52, scoreLabel: 13,
    statText: 18, statGap: 14, statMarginTop: 10,
    proofBadgeText: 16, proofBadgeIcon: 20, proofPad: '14px 18px',
    footerWebsite: 30, footerPhone: 19,
    ribbonText: 14, ribbonPad: '10px 18px',
    pagePad: '50px', gap: 22
  } : {  // landscape 1200×628
    logoH: 70,
    topBadge: 14, topBadgeIcon: 22, topBadgePad: '10px 18px',
    h1: 44, h1Accent: 44, subtitle: 16,
    cardPad: 18, accentHeight: 4,
    label: 11, emoji: 24, emojiGap: 8,
    name: 24, score: 36, scoreLabel: 11,
    statText: 13, statGap: 12, statMarginTop: 8,
    proofBadgeText: 13, proofBadgeIcon: 15, proofPad: '10px 16px',
    footerWebsite: 20, footerPhone: 13,
    ribbonText: 12, ribbonPad: '8px 16px',
    pagePad: '36px 50px', gap: 14
  };

  return (
    <div
      ref={posterRef}
      style={{
        width: `${dims.w}px`,
        height: `${dims.h}px`,
        background: 'linear-gradient(135deg, #0a1628 0%, #1e293b 50%, #0f172a 100%)',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}
    >
      {/* Decorative orbs */}
      <div style={{
        position: 'absolute', top: '-100px', right: '-100px',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(251,191,36,0.2), transparent 70%)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', bottom: '-150px', left: '-150px',
        width: '600px', height: '600px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.18), transparent 70%)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        pointerEvents: 'none'
      }} />

      {/* Content wrapper */}
      <div style={{
        position: 'relative',
        padding: sizes.pagePad,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isStory ? 'space-between' : 'center',
        gap: `${sizes.gap}px`
      }}>
        {/* Header: Logo + Badge */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '20px', flexWrap: 'nowrap'
        }}>
          <img
            src={GRRAS_LOGO_URL}
            alt="Grras"
            style={{ height: `${sizes.logoH}px`, objectFit: 'contain', flexShrink: 0 }}
          />
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: sizes.topBadgePad,
            background: 'linear-gradient(135deg, rgba(251,191,36,0.25), rgba(251,146,60,0.12))',
            border: '2px solid rgba(251,191,36,0.4)',
            borderRadius: '999px',
            flexShrink: 0,
            whiteSpace: 'nowrap'
          }}>
            <span style={{ fontSize: `${sizes.topBadgeIcon}px`, lineHeight: 1 }}>🏆</span>
            <span style={{
              color: '#fbbf24',
              fontSize: `${sizes.topBadge}px`,
              fontWeight: 900,
              letterSpacing: '0.2em',
              textTransform: 'uppercase'
            }}>
              Leaderboard
            </span>
          </div>
        </div>

        {/* Title */}
        <div style={{ textAlign: 'center' }}>
          <h1 style={{
            color: 'white',
            fontSize: `${sizes.h1}px`,
            fontWeight: 900,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            margin: 0,
            marginBottom: '14px'
          }}>
            Linux & DevOps
            <br />
            <span style={{
              background: 'linear-gradient(90deg, #fbbf24 0%, #fb923c 50%, #f87171 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: `${sizes.h1Accent}px`
            }}>
              Champions
            </span>
          </h1>
          <p style={{
            color: '#cbd5e1',
            fontSize: `${sizes.subtitle}px`,
            fontWeight: 700,
            letterSpacing: '0.15em',
            margin: 0,
            textTransform: 'uppercase'
          }}>
            {data?.period_labels?.weekly || 'This Week'}
          </p>
        </div>

        {/* Winners */}
        <div style={{
          display: 'flex',
          flexDirection: isLandscape ? 'row' : 'column',
          gap: `${sizes.gap - 6}px`,
          flex: isStory ? 'none' : 'initial'
        }}>
          <div style={{ flex: 1 }}>
            <WinnerCard
              winner={data?.all_time}
              label="All-Time Champion"
              emoji="👑"
              accentColor="#fbbf24"
              useFullName={useFullName}
              sizes={sizes}
            />
          </div>
          <div style={{ flex: 1 }}>
            <WinnerCard
              winner={data?.monthly}
              label="This Month's Hero"
              emoji="🔥"
              accentColor="#a78bfa"
              useFullName={useFullName}
              sizes={sizes}
            />
          </div>
          <div style={{ flex: 1 }}>
            <WinnerCard
              winner={data?.weekly}
              label="This Week's Warrior"
              emoji="⚡"
              accentColor="#34d399"
              useFullName={useFullName}
              sizes={sizes}
            />
          </div>
        </div>

        {/* Social proof strip */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isLandscape ? '24px' : '32px',
          padding: sizes.proofPad,
          background: 'rgba(255,255,255,0.04)',
          border: '1.5px solid rgba(255,255,255,0.1)',
          borderRadius: '18px',
          flexWrap: 'nowrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: `${sizes.proofBadgeIcon}px`, lineHeight: 1 }}>✓</span>
            <span style={{ fontSize: `${sizes.proofBadgeText}px`, fontWeight: 800 }}>VM-Verified</span>
          </div>
          <div style={{ width: '2px', height: `${sizes.proofBadgeIcon}px`, background: 'rgba(255,255,255,0.15)', flexShrink: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fbbf24', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: `${sizes.proofBadgeIcon}px`, lineHeight: 1 }}>⚡</span>
            <span style={{ fontSize: `${sizes.proofBadgeText}px`, fontWeight: 800 }}>250+ Labs</span>
          </div>
          <div style={{ width: '2px', height: `${sizes.proofBadgeIcon}px`, background: 'rgba(255,255,255,0.15)', flexShrink: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a78bfa', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: `${sizes.proofBadgeIcon}px`, lineHeight: 1 }}>🎯</span>
            <span style={{ fontSize: `${sizes.proofBadgeText}px`, fontWeight: 800 }}>No Shortcuts</span>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: '16px'
        }}>
          <div style={{ minWidth: 0, flexShrink: 1 }}>
            <p style={{
              color: 'white',
              fontSize: `${sizes.footerWebsite}px`,
              fontWeight: 900,
              margin: 0,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              whiteSpace: 'nowrap'
            }}>
              🌐 {GRRAS_WEBSITE}
            </p>
            <p style={{
              color: '#cbd5e1',
              fontSize: `${sizes.footerPhone}px`,
              fontWeight: 700,
              margin: 0,
              marginTop: '8px',
              lineHeight: 1.2,
              whiteSpace: 'nowrap'
            }}>
              📞 {GRRAS_PHONE}
            </p>
          </div>
          <div style={{
            padding: sizes.ribbonPad,
            background: 'linear-gradient(135deg, #fbbf24, #fb923c)',
            borderRadius: '12px',
            boxShadow: '0 12px 30px rgba(251,191,36,0.4)',
            flexShrink: 0
          }}>
            <p style={{
              color: '#1a1a1a',
              fontSize: `${sizes.ribbonText}px`,
              fontWeight: 900,
              margin: 0,
              letterSpacing: '0.1em',
              whiteSpace: 'nowrap'
            }}>
              REAL · VERIFIED · LIVE
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

PosterCanvas.displayName = 'PosterCanvas';

export const PromoPosterTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [format, setFormat] = useState('square');
  const [useFullName, setUseFullName] = useState(false);
  const [exporting, setExporting] = useState(false);
  const posterRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const t = getAdminToken();
      const res = await fetch(`${API_BASE}/api/admin/analytics/podium`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Podium fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    if (!posterRef.current) {
      alert('Poster not ready yet. Please wait and try again.');
      return;
    }
    setExporting(true);
    try {
      // Wait for all images inside poster to finish loading (prevents empty export)
      const imgs = Array.from(posterRef.current.querySelectorAll('img'));
      await Promise.all(imgs.map(img => {
        if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;  // don't block export on broken image
          setTimeout(resolve, 3000);  // safety timeout
        });
      }));

      // High-resolution export at native poster size
      const dataUrl = await toPng(posterRef.current, {
        cacheBust: true,
        pixelRatio: 2,  // Retina quality
        skipFonts: false,
        backgroundColor: '#0a1628'
      });

      if (!dataUrl || dataUrl === 'data:,') {
        throw new Error('Export generated empty image');
      }

      const link = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 10);
      link.download = `grras-leaderboard-${format}-${ts}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Poster export failed:', e);
      alert(`Export failed: ${e.message || 'Unknown error'}. Check browser console for details.`);
    } finally {
      setExporting(false);
    }
  };

  const copyCaption = () => {
    const caption = `🏆 This week's Linux & DevOps champions on Grras Lab!\n\n👑 ${data?.all_time?.display_name || 'Coming soon'} — ${data?.all_time?.labs_completed || 0} labs\n🔥 ${data?.monthly?.display_name || 'TBA'} (This Month)\n⚡ ${data?.weekly?.display_name || 'TBA'} (This Week)\n\n✅ VM-verified · ✅ 250+ hands-on labs · ✅ No shortcuts\n\nReal students. Real ranks. Real grind.\n\n🌐 ${GRRAS_WEBSITE}\n📞 ${GRRAS_PHONE}\n\n#GrrasLab #Linux #DevOps #RHCSA #HandsOnLearning`;
    navigator.clipboard.writeText(caption);
    alert('Caption copied to clipboard!');
  };

  const dims = FORMATS.find(f => f.value === format) || FORMATS[0];
  // Scale preview to fit on screen — poster is large, preview shrunk
  const previewScale = Math.min(1, 600 / dims.w);

  return (
    <div className="space-y-6" data-testid="promo-poster-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <ImageIcon className="w-6 h-6 text-amber-400" />
            Marketing Poster Generator
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            One-click branded posters for WhatsApp Status, Instagram, LinkedIn.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          className="border-white/20 text-white hover:bg-white/10"
          data-testid="poster-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Data
        </Button>
      </div>

      {/* Controls */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Format selector */}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">
                Format
              </label>
              <div className="flex flex-wrap gap-2">
                {FORMATS.map(f => (
                  <button
                    key={f.value}
                    onClick={() => setFormat(f.value)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition ${
                      format === f.value
                        ? 'bg-amber-500 text-slate-900 shadow-lg'
                        : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10'
                    }`}
                    data-testid={`poster-format-${f.value}`}
                  >
                    <div className="font-semibold">{f.label}</div>
                    <div className="text-[10px] opacity-80 mt-0.5">{f.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Privacy toggle */}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">
                Winner Names
              </label>
              <button
                onClick={() => setUseFullName(!useFullName)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                  useFullName
                    ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                    : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                }`}
                data-testid="poster-privacy-toggle"
              >
                {useFullName ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {useFullName ? 'Full Names (less private)' : 'First + Initial (recommended)'}
              </button>
              <p className="text-xs text-slate-500 mt-1">
                {useFullName
                  ? '⚠️ Shows "rohit sharma" — only share with winner consent'
                  : '✅ Shows "rohit S." — safe for public sharing'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                Preview ({Math.round(previewScale * 100)}% scale)
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Download will be full {dims.w}×{dims.h}px at 2× Retina quality
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copyCaption}
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="poster-copy-caption-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Copy Caption
              </Button>
              <Button
                onClick={handleExport}
                disabled={exporting || !data}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 hover:from-amber-600 hover:to-orange-600 font-semibold"
                data-testid="poster-download-btn"
              >
                <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
                {exporting ? 'Generating...' : 'Download PNG'}
              </Button>
            </div>
          </div>

          {/* Scaled preview wrapper */}
          <div className="bg-slate-900 rounded-xl p-4 overflow-auto flex justify-center">
            <div
              style={{
                width: `${dims.w * previewScale}px`,
                height: `${dims.h * previewScale}px`,
                position: 'relative'
              }}
            >
              <div
                style={{
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                  width: `${dims.w}px`,
                  height: `${dims.h}px`
                }}
              >
                <PosterCanvas
                  data={data}
                  format={format}
                  useFullName={useFullName}
                  posterRef={posterRef}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 border-violet-500/20">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-violet-300 flex items-center gap-2 mb-3">
            <Crown className="w-4 h-4" />
            Pro Tips for Maximum Reach
          </h3>
          <ul className="space-y-2 text-xs text-slate-300">
            <li>• Post between <span className="text-amber-300 font-semibold">9-10 AM</span> for WhatsApp Status peak visibility</li>
            <li>• Use <span className="text-amber-300 font-semibold">Story format</span> for WhatsApp Status (vertical 1080×1920)</li>
            <li>• Use <span className="text-amber-300 font-semibold">Landscape format</span> for LinkedIn/Twitter posts</li>
            <li>• Refresh data weekly (Sundays work best) to show fresh champions</li>
            <li>• First + Initial is safest — shares publicly without privacy concerns</li>
            <li>• Pair with "Copy Caption" button for ready-to-paste social text</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
